import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  getThemePaginationState,
  getThemeToggleClassName,
  themeButtonBase,
  themeControlDimensions,
  themeToneClasses,
  type ThemeControlSize,
  type ThemeSegment,
  type ThemeTone,
} from './themePresentation';

export type { ThemeTone } from './themePresentation';
export { themeTableClasses } from './themePresentation';

interface ThemeButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
  size?: ThemeControlSize;
  icon?: React.ReactNode;
  iconOnly?: boolean;
}

export const ThemeButton: React.FC<ThemeButtonProps> = ({ variant = 'ghost', size = 'sm', icon, iconOnly = false, children, className = '', type = 'button', ...props }) => {
  const tone = {
    primary: 'border-neon bg-neon text-surface hover:bg-primary-600',
    secondary: 'border-neon/25 bg-neon/10 text-neon hover:bg-neon/20',
    ghost: 'border-white/[0.08] bg-white/[0.025] text-muted hover:bg-white/[0.06] hover:text-foreground',
    danger: `${themeToneClasses.danger} hover:bg-error-500/20`,
    success: `${themeToneClasses.success} hover:bg-success-500/20`,
  }[variant];
  return <button {...props} type={type} className={`${themeButtonBase} ${themeControlDimensions(size, iconOnly)} ${tone} ${className}`}>{icon}{iconOnly ? <span className="sr-only">{children}</span> : children}</button>;
};

interface ThemeToggleButtonProps extends Omit<ThemeButtonProps, 'variant'> {
  label: string;
  selected?: boolean;
  segment?: ThemeSegment;
}

export const ThemeToggleButton: React.FC<ThemeToggleButtonProps> = ({ label, selected, segment, size = 'sm', icon, iconOnly = false, children, className = '', title, type = 'button', ...props }) => (
  <button {...props} type={type} aria-label={label} aria-pressed={selected} title={title ?? label} className={`${getThemeToggleClassName(selected, size, segment, iconOnly)} ${className}`}>
    {icon}{iconOnly ? <span className="sr-only">{children ?? label}</span> : children ?? label}
  </button>
);

export const ThemeToggleGroup: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className = '', ...props }) => (
  <div role="group" {...props} className={`inline-flex items-center rounded-md bg-transparent shadow-[inset_0_0_0_0.5px_rgba(255,255,255,0.08)] ${className}`} />
);

export const ThemeTabBar: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className = '', ...props }) => (
  <div role="group" {...props} className={`flex flex-wrap gap-2 border-b border-white/10 bg-surface px-4 py-2 ${className}`} />
);

interface ThemeSwitchProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'onClick' | 'children'> {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  size?: 'default' | 'small';
}

export const ThemeSwitch: React.FC<ThemeSwitchProps> = ({ label, checked, onCheckedChange, size = 'small', disabled, className = '', ...props }) => (
  <button {...props} type="button" role="switch" aria-label={label} aria-checked={checked} disabled={disabled} onClick={() => onCheckedChange(!checked)} className={`relative shrink-0 rounded-full border-[0.5px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon/60 disabled:cursor-not-allowed disabled:opacity-40 ${size === 'small' ? 'h-5 w-9' : 'h-6 w-11'} ${checked ? 'border-neon/30 bg-neon/25' : 'border-white/[0.08] bg-white/5'} ${className}`}>
    <span aria-hidden="true" className={`absolute left-0.5 top-1/2 -translate-y-1/2 rounded-full transition-transform ${size === 'small' ? 'h-3.5 w-3.5' : 'h-4 w-4'} ${checked ? `${size === 'small' ? 'translate-x-[1.125rem]' : 'translate-x-6'} bg-neon` : 'translate-x-0 bg-white/45'}`} />
  </button>
);

interface ThemeStatusProps extends React.HTMLAttributes<HTMLDivElement> {
  tone?: ThemeTone;
  icon?: React.ReactNode;
}

interface ThemeBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: ThemeTone;
  icon?: React.ReactNode;
}

export const ThemeBadge: React.FC<ThemeBadgeProps> = ({ tone = 'neutral', icon, children, className = '', ...props }) => (
  <span {...props} className={`inline-flex items-center gap-1.5 rounded-full border-[0.5px] px-2.5 py-1 text-[10px] font-semibold ${themeToneClasses[tone]} ${className}`}>{icon}{children}</span>
);

export const ThemeAlert: React.FC<ThemeStatusProps> = ({ tone = 'info', icon, children, className = '', ...props }) => (
  <div role={tone === 'danger' ? 'alert' : 'status'} {...props} className={`flex items-start gap-2 rounded-lg border-[0.5px] p-3 text-xs ${themeToneClasses[tone]} ${className}`}>{icon}{children}</div>
);

const inputClass = 'rounded-lg border-[0.5px] border-white/10 bg-surface text-foreground leading-none placeholder:text-muted focus:outline-none focus:border-neon/50 disabled:cursor-not-allowed disabled:opacity-40';

interface ThemeInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  controlSize?: ThemeControlSize;
}

interface ThemeSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  controlSize?: ThemeControlSize;
}

export const ThemeInput: React.FC<ThemeInputProps> = ({ controlSize = 'sm', className = '', ...props }) => <input {...props} className={`${themeControlDimensions(controlSize)} ${inputClass} ${className}`} />;
export const ThemeSelect: React.FC<ThemeSelectProps> = ({ controlSize = 'sm', className = '', ...props }) => <select {...props} className={`${themeControlDimensions(controlSize)} pr-8 ${inputClass} ${className}`} />;

interface ThemePaginationProps {
  label?: string;
  currentPage: number;
  totalPages?: number;
  hasNext?: boolean;
  onPageChange: (page: number) => void;
  compact?: boolean;
  disabled?: boolean;
}

export const ThemePagination: React.FC<ThemePaginationProps> = ({ label = 'Pagination', currentPage, totalPages, hasNext, onPageChange, compact = false, disabled = false }) => {
  const state = getThemePaginationState(currentPage, totalPages, hasNext);
  return (
    <nav aria-label={label} className="flex items-center gap-1">
      <ThemeButton iconOnly icon={<ChevronLeft className="h-3.5 w-3.5" />} aria-label="Previous page" title="Previous page" disabled={disabled || !state.canPrevious} onClick={() => onPageChange(state.page - 1)}>Previous page</ThemeButton>
      {compact || state.total === undefined
        ? <span className="min-w-12 px-1 text-center text-[10px] text-muted" aria-live="polite">{state.page}{state.total === undefined ? '' : ` / ${state.total}`}</span>
        : state.pages.map((page) => <ThemeToggleButton key={page} label={`Page ${page}`} aria-current={page === state.page ? 'page' : undefined} selected={page === state.page} disabled={disabled} onClick={() => onPageChange(page)}>{page}</ThemeToggleButton>)}
      <ThemeButton iconOnly icon={<ChevronRight className="h-3.5 w-3.5" />} aria-label="Next page" title="Next page" disabled={disabled || !state.canNext} onClick={() => onPageChange(state.page + 1)}>Next page</ThemeButton>
    </nav>
  );
};

interface ThemePaginationFooterProps {
  summary: React.ReactNode;
  children: React.ReactNode;
}

export const ThemePaginationFooter: React.FC<ThemePaginationFooterProps> = ({ summary, children }) => (
  <div className="mx-5 flex flex-wrap items-center justify-between gap-3 border-t border-white/5 px-4 py-3">
    <p className="text-xs font-medium text-white/60">{summary}</p>
    {children}
  </div>
);
